import { useNavigate } from 'react-router-dom';
import { Icon } from './Icon';
import { cn } from '@/lib/cn';

interface CloseProfileButtonProps {
  /** Optional explicit handler. If omitted, falls back to `navigate(-1)`. */
  onClose?: () => void;
  /** Optional class overrides — position, z-index, custom offsets. */
  className?: string;
  /** Accessible label. Default: "Close profile". */
  label?: string;
}

/**
 * Top-right X overlay used on every full-screen profile/detail view
 * across .app / .vip / .tv. Renders absolutely-positioned, dark ink
 * pill with a hairline border and pink glow on hover.
 *
 * Position is `absolute top-4 right-4` by default — the parent must be
 * `relative` (or a stacking context). Override via `className` for
 * different offsets / z-indices.
 */
export const CloseProfileButton = ({
  onClose,
  className,
  label = 'Close profile',
}: CloseProfileButtonProps) => {
  const navigate = useNavigate();
  const handle = () => {
    if (onClose) onClose();
    else navigate(-1);
  };

  return (
    <button
      type="button"
      onClick={handle}
      aria-label={label}
      className={cn(
        'absolute right-4 top-4 z-30 inline-flex h-10 w-10 items-center justify-center',
        'border border-white/[0.10] bg-ink/85 text-text-secondary backdrop-blur',
        'transition-[color,border-color,background] duration-fast ease-signal',
        'hover:border-pink hover:bg-pink/[0.10] hover:text-pink hover:shadow-glow-soft',
        'md:right-6 md:top-6 md:h-11 md:w-11',
        className,
      )}
    >
      <Icon name="close" size={18} />
    </button>
  );
};
